import * as ko from 'knockout';
import i18n from '../../i18n';

import * as dimensionMetasApi from '../../api/dimension_metas';
import * as dimensionsApi from '../../api/dimensions';
import { BaseTrialStep } from './base';
import { WizardController } from '../../screens/trial_wizard';
import { DatasetDimensionMeta, LimitToDimension } from '../../models/dataset_dimension_meta';
import { DimensionMeta } from '../../models/dimension_meta';
import { DatasetDimensionMetasEditConfiguration } from '../../components/dataset_dimension_metas_edit';
import { translate } from '../../i18n_text';
import { ImportEntitiesDelegate } from '../import_entities';
import { buildDuplicateActionOptions, runManualResolutionFlow } from '../resolve_import_conflicts';
import {
  CustomLayoutData,
  commitFactorsImport,
  downloadCustomLayoutImportTemplate,
  downloadFactorsImportTemplate,
  validateFactorsImport,
} from '../../api/trials';
import { encodeArrayBufferToBase64 } from '../../utils';
import { confirmDialog } from '../confirm_dialog';
import { logError } from '../../error_logging';
import { CUSTOM_LAYOUT_VALID_PLOT_DESIGNS } from '../../models/trial';
import { session } from '../../session';
import { allowViewLimitToDimensionNameAndAttributes, canReorderTrialLimitTo } from '../../permissions';

let template =
  require('raw-loader!../../../templates/components/trial_wizard/trial_test_subject.html').default;

interface ConfirmTSChangeOptions {
  resetTreatments?: boolean;
  leadWarnings?: string[];
}

class TrialTestSubject extends BaseTrialStep {
  title =
    SERVER_INFO.USE_FACTORS_NAMING ||
      session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
      ? i18n.t('Step 2 - Select the trial treatment factors')
      : i18n.t('Step 2 - Select the trial test subject');
  subtitle =
    SERVER_INFO.USE_FACTORS_NAMING ||
      session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
      ? i18n.t('Choose your treatment factors for this trial')
      : i18n.t('Choose your test subject for this trial');
  addMultipleText =
    SERVER_INFO.USE_FACTORS_NAMING ||
      session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
      ? i18n.t('Add multiple treatment factors.')
      : i18n.t('Add multiple test subjects.');
  removingControlNonControlText = i18n.t('Remove control:non-control combinations.');

  subjectTypes = [
    {
      name: i18n.t('Crop variety'),
      slug: dimensionMetasApi.CROP_VARIETY_SLUG,
      icon: require('../../../images/trial_wizard/crop_variety.png'),
      question: i18n.t('Which crop varieties do you want to test?'),
    },
    {
      name: i18n.t('Fertilizer'),
      slug: dimensionMetasApi.FERTILIZER_SLUG,
      icon: require('../../../images/trial_wizard/fertilizer.png'),
      question: i18n.t('Which fertilizers do you want to test?'),
    },
    {
      name: i18n.t('Chemical'),
      slug: dimensionMetasApi.CHEMICAL_SLUG,
      icon: require('../../../images/trial_wizard/chemical.png'),
      question: i18n.t('Which chemicals do you want to test?'),
    },
    {
      name: i18n.t('Protocol / Treatment program'),
      slug: dimensionMetasApi.PROTOCOL_SLUG,
      icon: require('../../../images/trial_wizard/protocol.png'),
      question: i18n.t('Which protocols do you want to test?'),
    },
    {
      name: i18n.t('Custom / Other'),
      slug: null,
      icon: require('../../../images/trial_wizard/custom.png'),
      question: i18n.t('Which subjects do you want to test?'),
    },
  ];

  subjectTypeSlugs = [
    dimensionMetasApi.CROP_VARIETY_SLUG,
    dimensionMetasApi.FERTILIZER_SLUG,
    dimensionMetasApi.CHEMICAL_SLUG,
    dimensionMetasApi.PROTOCOL_SLUG,
  ];

  selectedSubjectTypes = ko.observableArray<KnockoutObservable<boolean>>(
    this.subjectTypes.map(() => ko.observable(false))
  );

  isTreatmentManagementEnabled = session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial());

  // is multiple test subjects mode on?
  multipleSubjects = ko.observable<boolean>();

  // map of known dimension metas
  dimensionMetas = ko.observable<{ [key: string]: DimensionMeta }>({});

  customSubjectTypeBeingRemoved = ko.observable<boolean>(false);
  globalError = ko.observable('');
  stagesError = ko.observable('');

  testSubjectsConfig = ko.pureComputed(() => {
    let res: DatasetDimensionMetasEditConfiguration = {
      user: this.trialWizard().userData,

      addDMText: i18n.t('Add Subject Type')(),
      selectDMText: i18n.t('Select Subject Type')(),
      emptyDDMWarning: null,
      emptyDimensionsWarning: null,
      addDimensionText: (ddm: DatasetDimensionMeta) => {
        let dmName = ddm.dimensionMeta() ? translate(ddm.dimensionMeta().nameJson()) : i18n.t('Subject')();
        return i18n.t('Select a {{ dimension_meta }}', {
          dimension_meta: dmName.toLocaleLowerCase(),
        })();
      },

      canEditRequired: () => false,
      canEditDimensionMeta: (ddm: DatasetDimensionMeta) => !ddm.isPredefined() && this.allowEditAny(),
      trialWizard: this.trialWizard(),
      allowAnonymize: true,
      allowEditOptional: false,
      allowIncludeInFactorialCombinations: session.isTreatmentManagementEnabledForTrial(
        this.trialWizard().trial()
      ),
      multipleSubjectsOptionEnabled: this.multipleSubjects(),
      allowAdd: () => false,
      allowEdit: this.allowEdit,
      allowEditAny: this.allowEditAny,
      allowViewLimitToDimensionNameAndAttributes: allowViewLimitToDimensionNameAndAttributes(),
      allowRemove: (ddm: DatasetDimensionMeta) => {
        if (!this.allowEdit()) {
          return false;
        }

        return !ddm.isPredefined() && this.trialWizard().testSubjects().length > 1;
      },

      datasetDimensionMetas: this.trialWizard().testSubjects,
      createDatasetDimensionMeta: this.createDDM,

      confirmEditEntity: () => this.confirmTSChange({ resetTreatments: true }),
      confirmChangeEntities: () => this.confirmTSChange(),
      allowReorder: canReorderTrialLimitTo(this.trialWizard().userData, this.trialWizard().trial()),
      excludeDimensionMetaSlugs: [dimensionMetasApi.SITE_SLUG, dimensionMetasApi.REPETITION_SLUG],
    };

    return res;
  });

  showImportCustomLayout = ko.observable(false);

  importCustomLayoutDelegate: ImportEntitiesDelegate<CustomLayoutData> = {
    title: i18n.t('Import custom layout'),
    description: i18n.t(
      'Download the Excel template and upload the modified file containing your custom layout.'
    )(),
    backTitle: '',
    templateBaseName: 'layout',
    downloadTemplate: () => {
      const subjects = this.trialWizard().testSubjects();
      const controlIds: string[] = [];
      for (let ddm of subjects) {
        for (let dim of ddm.limitTo()) {
          if (dim.control()) {
            controlIds.push(dim.id());
          }
        }
      }

      return downloadCustomLayoutImportTemplate({
        dm_ids: subjects.map((ts) => ts.dimensionMeta()?.id()),
        control_dim_ids: controlIds,
        existing: this.trialWizard().plotGuides(),
        trial_id: this.trialWizard().trial().id(),
        template_id: this.trialWizard().trial().createdFromTemplateId,
        treatments: this.trialWizard()
          .treatments()
          .map((treatment) => treatment.toData()),
      });
    },
    importUrl: '/api/trials/import_custom_layout/',
    prepareFileContents: (fileContents) =>
      JSON.stringify({
        file_contents: encodeArrayBufferToBase64(fileContents),
        crop_id: this.trialWizard().trial().crop()?.id ?? null,
        dm_ids: this.trialWizard()
          .testSubjects()
          .map((ts) => ts.dimensionMeta()?.id()),
        trial_id: this.trialWizard().trial().id(),
      }),
    onSuccess: async (data) => {
      const errors = await this.trialWizard().applyCustomLayout(data);

      if (this.isTreatmentManagementEnabled && this.controller?.wizardScreen && errors.length === 0) {
        const treatmentsStepIndex = this.controller.wizardScreen
          .wizardSteps()
          .findIndex((step) => step.componentName === 'trial-treatments');
        if (treatmentsStepIndex >= 0) {
          this.controller.wizardScreen.enableStep(treatmentsStepIndex);
        }
      }

      return errors.map((message) => ({ sheet: '', cell: '', message }));
    },
  };
  disableCustomLayoutActions = ko.pureComputed(() => {
    const subjects = this.trialWizard()?.testSubjects() ?? [];
    const plotDesign = this.trialWizard()?.trial()?.plotDesign();
    return !(
      this.allowEditAny() &&
      subjects.length > 0 &&
      subjects.every((ts) => !!ts.dimensionMeta()) &&
      CUSTOM_LAYOUT_VALID_PLOT_DESIGNS.indexOf(plotDesign) > -1
    );
  });

  showImportFactors = ko.observable(false);

  duplicateActionOptions = buildDuplicateActionOptions();

  duplicateAction = ko.observable<dimensionsApi.ImportDimensionsDuplicatesAction>('manual');

  // Set by the import's onSuccess: the library counts alone don't tell the user that ignored
  // (already existing) records were still attached to the trial as factors.
  importResultNote = ko.observable<string | null>(null);

  canShowImportFactors = ko.pureComputed(() => {
    const subjects = this.trialWizard()?.testSubjects() ?? [];
    return this.allowEditAny() && subjects.length > 0 && subjects.every((ts) => !!ts.dimensionMeta());
  });

  disableImportFactors = ko.pureComputed(() => {
    return !this.canShowImportFactors();
  });

  importFactorsDelegate!: ImportEntitiesDelegate<string[]> & {
    duplicateAction: KnockoutObservable<dimensionsApi.ImportDimensionsDuplicatesAction>;
    duplicateActionOptions: { id: dimensionsApi.ImportDimensionsDuplicatesAction; name: string }[];
  };

  private subscriptions: KnockoutSubscription[] = [];
  private isMultipleTestSubjectsChangedReentrant = false;

  constructor(params: { controller: WizardController }) {
    super(params);

    this.importFactorsDelegate = {
      title: i18n.t('Import Factors'),
      description: i18n.t(
        'Download the Excel template and upload the modified file containing your factor values. All imported dimensions will be automatically added to the trial as factors.'
      )(),
      backTitle: '',
      templateBaseName: 'factors',
      duplicateAction: this.duplicateAction,
      duplicateActionOptions: this.duplicateActionOptions,
      resultNote: this.importResultNote,
      downloadTemplate: () => {
        const testSubjects = this.trialWizard().testSubjects();
        const dimensionMetaIds = testSubjects
          .filter((ts) => ts.dimensionMeta())
          .map((ts) => ts.dimensionMeta()!.id());
        return downloadFactorsImportTemplate(dimensionMetaIds);
      },
      importUrl: () => {
        return `/api/trials/import_factors/?duplicate_action=${this.duplicateAction()}`;
      },
      manualResolution: {
        enabled: ko.pureComputed(() => this.duplicateAction() === 'manual'),
        resolve: (fileContents: ArrayBuffer) =>
          runManualResolutionFlow(fileContents, {
            validate: validateFactorsImport,
            commit: commitFactorsImport,
          }),
      },
      onSuccess: async (importedDimensionIds: string[]) => {
        if (importedDimensionIds.length === 0) {
          this.reload();
          return [];
        }

        const testSubjects = this.trialWizard().testSubjects();
        const uniqueImportedIds = new Set(importedDimensionIds);
        const idsBelongingToTrialFactors = new Set<string>();
        let addedToTrialCount = 0;

        for (const testSubject of testSubjects) {
          const dm = testSubject.dimensionMeta()!;
          const dimensionDataList = await dimensionsApi.list(dm.id(), { ids: importedDimensionIds }, {});

          const limitToDimensionsById = new Map(testSubject.limitTo().map((d) => [d.id(), d]));
          const newLimitTos: LimitToDimension[] = [];
          let refreshedAnyLimitTo = false;

          for (const dimData of dimensionDataList) {
            idsBelongingToTrialFactors.add(dimData.id);

            const alreadyAFactorOfTheTrial = limitToDimensionsById.get(dimData.id);
            if (alreadyAFactorOfTheTrial) {
              // An updated record keeps its id, so it is never pushed below. Without this refresh the
              // table would go on showing the values loaded when the wizard opened.
              alreadyAFactorOfTheTrial.initFromData(dimData, dm);
              refreshedAnyLimitTo = true;
            } else {
              newLimitTos.push(new LimitToDimension(this.trialWizard(), dm, dimData));
            }
          }

          if (newLimitTos.length > 0) {
            testSubject.limitTo.push(...newLimitTos);
            addedToTrialCount += newLimitTos.length;
          } else if (refreshedAnyLimitTo) {
            // dimensions-table writes its cells imperatively and repaints only when the array itself
            // notifies, so an in-place refresh has to announce the change.
            testSubject.limitTo.valueHasMutated();
          }
        }

        this.importResultNote(
          this.describeImportedFactors(
            addedToTrialCount,
            uniqueImportedIds.size - idsBelongingToTrialFactors.size
          )
        );
        this.reload();

        return [];
      },
    };

    this.subscriptions.push(this.multipleSubjects.subscribe(this.onMultipleSubjectsChanged));
    this.subscriptions.push(this.trialWizard().testSubjects.subscribe(this.onTestSubjectsChanged));
    this.onTrialWizardChanged();
    this.subscriptions.push(this.trialWizard.subscribe(this.onTrialWizardChanged));

    // get predefined test subject dimension metas
    // OPT: possibly convert to a single API call
    Promise.all(this.subjectTypeSlugs.map((slug) => dimensionMetasApi.retrieve(slug))).then((dmDatas) => {
      dmDatas.forEach((d) => {
        this.dimensionMetas()[d.slug] = new DimensionMeta(d);
      });

      this.reload();
    });
  }

  dispose() {
    for (let subscription of this.subscriptions) {
      subscription.dispose();
    }
  }

  onTestSubjectsChanged = (newTestSubjects: any) => {
    if (newTestSubjects.length < 2) {
      this.trialWizard().excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects(false);
    }
  };

  reload() {
    let ddms = this.trialWizard().testSubjectsWithDimensionMeta();
    let slugs = ddms.map((ddm) => ddm.dimensionMeta().slug());

    if (ddms.length > 1) {
      this.multipleSubjects(true);
    }

    slugs.forEach((slug) => {
      let index = this.subjectTypeSlugs.indexOf(slug);
      index = index >= 0 ? index : this.subjectTypes.length - 1;

      this.selectedSubjectTypes()[index](true);
    });

    this.ready(true);
  }

  selectSubjectType = async (subjectType: { slug: string }) => {
    if (!this.allowEdit()) {
      return;
    }
    let curIdx = this.subjectTypes.map((s) => s.slug).indexOf(subjectType.slug);
    let curVal = this.selectedSubjectTypes()[curIdx]();
    const hasTreatments = this.trialWizard().treatments().length > 0;

    if (hasTreatments) {
      try {
        await confirmDialog(
          curVal ? i18n.t('Removing treatment factors')() : i18n.t('Adding treatment factors')(),
          curVal
            ? i18n.t(
              'You have already added treatments. Removing factors will remove all treatments. Are you sure you want to continue?'
            )()
            : i18n.t(
              'You have already added treatments. Adding factors will remove all treatments. Are you sure you want to continue?'
            )()
        );
      } catch (e) {
        // The user rejected the dialog
        return;
      }
    }

    // The treatments are cleared only once the plot change is confirmed too, since that confirmation
    // is what schedules the plot regeneration.
    if (!(await this.tryConfirmTSChange())) {
      return;
    }

    if (hasTreatments) {
      this.trialWizard().treatments([]);
    }

    this.doSelectSubjectType(subjectType);
  };

  private itemsText(): string {
    return SERVER_INFO.USE_FACTORS_NAMING ||
      session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
      ? i18n.t(['treatment_factor_lowercase', 'treatment factors'])()
      : i18n.t('test subjects')();
  }

  private async tryConfirmTSChange(options: ConfirmTSChangeOptions = {}): Promise<boolean> {
    try {
      await this.confirmTSChange(options);
      return true;
    } catch (e) {
      // the confirmation rejects with null when the user cancels; anything else is a real failure
      if (e) {
        logError('confirm test subjects change', e);
      }

      return false;
    }
  }

  private async confirmTSChange({
    resetTreatments = false,
    leadWarnings = [],
  }: ConfirmTSChangeOptions = {}): Promise<{}> {
    const text = this.itemsText();

    const hasTreatments = resetTreatments && this.trialWizard().treatments().length > 0;

    if (hasTreatments) {
      await this.trialWizard().confirmChangeResettingTreatments(
        text,
        !!this.trialWizard().plotGuides(),
        leadWarnings
      );
      this.trialWizard().treatments([]);
    } else {
      await this.trialWizard().confirmChangeAffectingPlots(
        text,
        !!this.trialWizard().plotGuides(),
        leadWarnings
      );
    }

    this.trialWizard().plotGuides(null);
    this.trialWizard().savePlotGuides = true;

    return null;
  }

  private doSelectSubjectType(subjectType: { slug: string }) {
    let curIdx = this.subjectTypes.map((s) => s.slug).indexOf(subjectType.slug);
    let curSlug = subjectType.slug;
    let curVal = this.selectedSubjectTypes()[curIdx]();
    let customCount = this.trialWizard()
      .testSubjects()
      .filter((ddm) => !ddm.isPredefined()).length;

    // removing multiple custom types?
    if (curIdx === this.subjectTypes.length - 1 && curVal && customCount > 1) {
      this.customSubjectTypeBeingRemoved(true);
      return;
    }

    let toRemove: number[] = [];

    if (!this.multipleSubjects()) {
      this.selectedSubjectTypes().forEach((obs, index) => {
        if (obs()) {
          toRemove.push(index);
        }
        obs(false);
      });
    } else if (curVal) {
      toRemove.push(curIdx);
    }

    this.selectedSubjectTypes()[curIdx](!curVal);

    toRemove.forEach((index) => {
      let slug = this.subjectTypes[index].slug;
      this.removeDDM(slug);
    });

    if (!curVal) {
      this.addDDM(curSlug);
    }
  }

  onMultipleSubjectsChanged = async (newValue: boolean) => {
    if (!this.ready() || this.isMultipleTestSubjectsChangedReentrant) {
      return;
    }

    const leadWarnings = newValue ? [] : this.removeExtraTestSubjectsWarnings();

    // restore old value (which is equal to !newValue) while the user is being asked to confirm, and
    // apply the new value only once the change goes through
    this.setMultipleSubjectsSilently(!newValue);

    if (!(await this.tryConfirmTSChange({ leadWarnings }))) {
      return;
    }

    this.setMultipleSubjectsSilently(newValue);
    this.doChangeMultipleTestSubjects(newValue);
  };

  private setMultipleSubjectsSilently(value: boolean) {
    this.isMultipleTestSubjectsChangedReentrant = true;
    this.multipleSubjects(value);
    this.isMultipleTestSubjectsChangedReentrant = false;
  }

  /**
   * Warn about the test subjects that switching multiple subjects off removes permanently.
   *
   * @returns no warnings when nothing would be removed
   */
  private removeExtraTestSubjectsWarnings(): string[] {
    const total = this.trialWizard().testSubjects().length;

    if (total < 2) {
      return [];
    }

    const items = this.itemsText();
    const warnings = [
      i18n.t(
        [
          'remove_extra_test_subjects_warning',
          'Turning off multiple {{ items }} will permanently remove {{ removed }} of the {{ total }} {{ items }} you added, together with the elements you selected for them.',
        ],
        { items, removed: total - 1, total }
      )(),
    ];

    if (this.trialWizard().treatments().length > 0) {
      warnings.push(
        i18n.t([
          'remove_extra_test_subjects_treatments_warning',
          'The treatments that use them will be removed as well.',
        ])()
      );
    }

    return warnings;
  }

  private doChangeMultipleTestSubjects(newValue: boolean) {
    // keep a single test subject if multiple subjects switched off
    if (newValue) {
      return;
    }

    const subjects = this.trialWizard().testSubjects();

    if (subjects.length > 1) {
      this.trialWizard().testSubjects([subjects[0]]);
      this.setOrder();
      // the confirmation already announced the treatments that go away with the removed subjects
      this.trialWizard().onTestSubjectsUpdated(false);
    }

    // the selected subject type must describe the subject that is left, whichever order the types
    // were picked in
    const remainingTypeIndex = this.singleTestSubjectTypeIndex();

    this.selectedSubjectTypes().forEach((subjectTypeSelected, index) => {
      subjectTypeSelected(index === remainingTypeIndex);
    });

    this.trialWizard().excludeFromGenerationCombinationsOfControlAndNonControlTestSubjects(false);
  }

  private singleTestSubjectTypeIndex(): number {
    const subjects = this.trialWizard().testSubjects();

    if (subjects.length === 0) {
      return -1;
    }

    // the observable holds null until a dimension meta is picked for a custom subject
    const dimensionMeta: DimensionMeta | null = subjects[0].dimensionMeta();
    const slugIndex = dimensionMeta ? this.subjectTypeSlugs.indexOf(dimensionMeta.slug()) : -1;

    // custom and unknown subject types share the last type
    return slugIndex >= 0 ? slugIndex : this.subjectTypes.length - 1;
  }

  onTrialWizardChanged = () => {
    this.subscriptions.push(this.trialWizard().trial().crop.subscribe(this.onCropChanged));
  };

  onCropChanged = () => {
    for (let ddm of this.trialWizard().testSubjects()) {
      if (ddm.dimensionMeta() && ddm.dimensionMeta().slug() === dimensionMetasApi.CROP_VARIETY_SLUG) {
        ddm.limitTo([]);
      }
    }
    this.trialWizard().onTestSubjectsUpdated();
  };

  confirmCustomSubjectTypeRemove = () => {
    if (!this.customSubjectTypeBeingRemoved()) {
      return;
    }

    this.selectedSubjectTypes()[this.subjectTypes.length - 1](false);
    this.removeDDM(null);

    this.customSubjectTypeBeingRemoved(false);
  };

  cancelCustomSubjectTypeRemove = () => {
    this.customSubjectTypeBeingRemoved(false);
  };

  /**
   * Add another custom subject type.
   *
   * Handler for "Add another" custom subject type button.
   */
  addCustomSubjectType = async () => {
    try {
      if (this.trialWizard().treatments().length > 0) {
        await confirmDialog(
          i18n.t('Adding treatment factors')(),
          i18n.t(
            'You have already added treatments. Adding factors will remove all treatments. Are you sure you want to continue?'
          )()
        );
        this.trialWizard().treatments([]);
      }
      if (await this.tryConfirmTSChange()) {
        this.selectedSubjectTypes()[this.subjectTypes.length - 1](true);
        this.addDDM(null);
      }
    } catch (e) {
      // The user rejected the dialog
      return;
    }
  };

  /**
   * Create empty dataset dimension meta to be used as a test subject.
   *
   * Low level method called from other components and higher level logic.
   *
   * @param slug  optional dimension meta slug to initialize ddm with one of
   *     known dimension metas
   * @param isPredefined  optional indicator for ddms that correspond to
   *     visual buttons outside of ddm table
   */
  private createDDM = (slug?: string, isPredefined?: boolean) => {
    let ddm = new DatasetDimensionMeta(this.trialWizard());
    ddm.useForPlot(true);

    if (slug) {
      ddm.dimensionMeta(this.dimensionMetas()[slug] || null);
    }
    if (isPredefined) {
      ddm.isPredefined(isPredefined);
    }

    return ddm;
  };

  /**
   * Add a test subject with given slug.
   *
   * @param slug  dimension meta slug for new DDM. null for custom/other subjects
   */
  private addDDM = (slug: string) => {
    let ddm = this.createDDM(slug, slug !== null);
    this.trialWizard().testSubjects.push(ddm);
    this.setOrder();
    this.globalError('');
  };

  /**
   * Remove test subjects with given slug.
   *
   * When dealing with custom subjects this will remove all of them
   *
   * @param slug  dimension meta slug for new DDM. null for custom/other subjects
   */
  private removeDDM = (slug: string) => {
    let removePredefined = slug !== null;

    this.trialWizard().testSubjects.remove((ddm) => {
      let ddmSlug = ddm.dimensionMeta() ? ddm.dimensionMeta().slug() : null;

      // removing by slug can only remove ddms added by the buttons
      if (removePredefined && ddmSlug === slug && ddm.isPredefined()) {
        return true;
      }

      // removing custom removes everything not added by specific buttons
      return !removePredefined && !ddm.isPredefined();
    });
    this.setOrder();
    this.trialWizard().onTestSubjectsUpdated();
  };

  private setOrder() {
    let testSubjects = this.trialWizard().testSubjects();
    for (let i = 0; i < testSubjects.length; i++) {
      testSubjects[i].order(i);
    }
  }

  hasErrors() {
    for (let ddm of this.trialWizard().testSubjects()) {
      if (ddm.hasErrors()) {
        return true;
      }
    }

    return !!this.stagesError() || !this.isOneTestSubjectSelected();
  }

  applyLocalValidation(): boolean {
    this.globalError('');
    this.stagesError('');

    if (!this.isOneTestSubjectSelected()) {
      let text =
        SERVER_INFO.USE_FACTORS_NAMING ||
          session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
          ? i18n.t('Select one treatment factor to continue')
          : i18n.t('Select one test subject to continue');
      this.globalError(text());

      return false;
    }

    return true;
  }

  private isOneTestSubjectSelected() {
    return this.selectedSubjectTypes().filter((obs) => obs()).length > 0;
  }

  clearServerErrors() {
    for (let ddm of this.trialWizard().testSubjects()) {
      this.clearModelServerErrors(ddm);
    }
  }

  applyServerErrors(errors: any) {
    let stagesError = errors['test_subjects_stages'];
    if (stagesError) {
      this.stagesError(stagesError);
    }

    let testSubjectsErrors = errors['test_subjects'] || [];
    let testSubjects = this.trialWizard().testSubjects();

    for (let i = 0; i < testSubjectsErrors.length; i++) {
      let ddm = testSubjects[i];
      let dmError = testSubjectsErrors[i]['dimension_meta_id'];
      if (dmError) {
        this.setServerError(ddm.dimensionMeta, dmError);
      }
    }
  }

  openImportCustomLayout = async () => {
    if (this.disableCustomLayoutActions()) {
      return;
    }

    if (!this.trialWizard().canSafelyRegeneratePlots()) {
      let title = i18n.t('Changing plots')();
      let message = i18n.t(
        'You have customized the plots. After importing a new layout your customizations will be lost. Are you sure you want to continue?'
      )();

      await confirmDialog(title, message);
    }

    this.showImportCustomLayout(true);
  };

  closeImportCustomLayout = () => {
    this.showImportCustomLayout(false);
  };

  resetCustomLayout = async () => {
    if (this.disableCustomLayoutActions()) {
      return;
    }

    await this.tryConfirmTSChange();
  };

  // `orphanCount` are records the import created or updated in the library that belong to none of
  // this trial's factor types — a template downloaded for factors that were since removed from the
  // trial, say. They are reported on their own line so the trial-side counts stay accurate.
  private describeImportedFactors(addedToTrialCount: number, orphanCount: number): string {
    const sentences: string[] = [];

    if (addedToTrialCount === 1) {
      sentences.push(i18n.t('One record was added to the trial as a factor.')());
    } else if (addedToTrialCount > 1) {
      sentences.push(
        i18n.t('{{count}} records were added to the trial as factors.', { count: addedToTrialCount })()
      );
    } else if (orphanCount === 0) {
      sentences.push(i18n.t('All imported records were already factors of this trial.')());
    }

    if (orphanCount === 1) {
      sentences.push(i18n.t("One imported record belongs to none of this trial's factor types.")());
    } else if (orphanCount > 1) {
      sentences.push(
        i18n.t("{{count}} imported records belong to none of this trial's factor types.", {
          count: orphanCount,
        })()
      );
    }

    return sentences.join(' ');
  }

  openImportFactors = async () => {
    if (this.disableImportFactors()) {
      return;
    }

    this.showImportFactors(true);
  };

  closeImportFactors = () => {
    this.showImportFactors(false);
  };
}

ko.components.register('trial-test-subject', {
  viewModel: TrialTestSubject,
  template: template,
});
